import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import images from "../../assets/images";
import "../../index.css";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ValidationModule,
  themeQuartz,
  TextEditorModule,
  ColDef,
  ICellRendererParams,
  GridReadyEvent,
} from "ag-grid-community";
import {
  ChevronDown,
  QrCode,
  Download,
  Eye,
  EyeOff,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowBigRightDash,
} from "lucide-react";
import Modal from "../../components/modal/Modal";
import MaintenanceModal from "../../components/modal/Modal";
import AddAssets from "./AddAssets";
import {
  useAsset,
  useEquipmentStore,
  usePremisesStore,
} from "../../store/store";
import { Dropdown } from "../../components/dropdown/Dropdown";
import Maintenance from "./Maintenance";
import { useAuth } from "../../auth/AuthProvider";
import { usePermissions } from "../../hooks/usePermission";
import { ASSETS_EQUIPMENT } from "../../constants/modules";
import { PremisesData } from "../Budgeting/types/budgeting";
import ImportAssetsModal from "../../components/ImportAssetsModal/ImportAssetsModal";
import axios from "axios";
import { proxyUrl } from "../../common/utils";
import axiosInstance from "../../common/axiosInstance";
import QRCodeModal from "../../components/QRCodeModal/QRCodeModal";
import EquipmentDashboard from "../../components/SafetyEquipment/EquipmentDashboard";
import {
  getPaginatedAssets,
  PaginatedAssetsParams,
} from "../../service/api";
import Pagination from "../../components/ui/Pagination";
import { LeftCircleFilled } from "@ant-design/icons";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextEditorModule,
  ValidationModule,
]);

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

// Dynamic column configuration based on equipment type patterns
const getEquipmentTypeColumns = (equipmentTypeName: string): ColDef[] => {
  const name = equipmentTypeName?.toLowerCase() || "";

  // Fire Extinguisher variations (extinguisher, extinguishers, fire extinguisher, etc.)
  if (name.includes("extinguisher")) {
    return [
      {
        field: "cylinderNumber",
        headerName: "CYLINDER NO",
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
      },
      {
        field: "capacity",
        cellEditor: "agTextCellEditor",
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1,
        minWidth: 100,
        cellEditorParams: {
          maxLength: 20,
        },
        headerName: "CAPACITY",
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
        filter: false,
      },
      {
        field: "type",
        headerName: "TYPE",
        minWidth: 100,
        flex: 1,
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
        } as any,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "floor",
        headerName: "Floor",
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1,
        minWidth: 80,
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "left",
        } as any,
      },
      {
        field: "location",
        headerName: "LOCATION",
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1.5,
        minWidth: 150,
        filter: false,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        } as any,
      },
      {
        field: "hptTestDate",
        headerName: "HPT NO",
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "left",
        } as any,
      },
      {
        field: "lastRefillDate",
        headerName: "Last Refill Date",
        minWidth: 140,
        flex: 1,
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "refillRequired",
        headerName: "Refill Required",
        minWidth: 130,
        flex: 0.8,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => {
          const required = !!params.data?.refillRequired;
          return (
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${required
                ? "bg-red-100 text-red-700 border border-red-300"
                : "bg-green-50 text-green-700 border border-green-200"
                }`}
            >
              {required ? "Yes" : "No"}
            </span>
          );
        },
      },
      {
        field: "hptRequired",
        headerName: "HPT Required",
        minWidth: 120,
        flex: 0.8,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => {
          const required = !!params.data?.hptRequired;
          return (
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${required
                ? "bg-red-100 text-red-700 border border-red-300"
                : "bg-green-50 text-green-700 border border-green-200"
                }`}
            >
              {required ? "Yes" : "No"}
            </span>
          );
        },
      },
    ];
  }

  // Fire Door variations (door, doors, fire door, fire doors, etc.)
  if (name.includes("door")) {
    return [
      {
        field: "description",
        headerName: "Description",
        minWidth: 200,
        flex: 2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
        } as any,
        cellRenderer: (params: any) => (
          <div
            style={{
              whiteSpace: "normal",
              lineHeight: "1.4",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              padding: "4px 0",
              maxWidth: "100%",
            }}
            title={params.value}
          >
            {params.value || "N/A"}
          </div>
        ),
      },
      {
        field: "location",
        headerName: "Location",
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1,
        filter: false,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "qty",
        headerName: "Quantity",
        minWidth: 100,
        flex: 0.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    ];
  }

  // Smoke Detector variations (smoke detector, smoke detectors, detector, detectors, etc.)
  // if (name.includes("smoke") || name.includes("detector")) {
  //   return [
  //     {
  //       field: "description",
  //       headerName: "Description",
  //       flex: 2,
  //       minWidth: 200,
  //       wrapText: true,
  //       autoHeight: true,
  //       headerStyle: { backgroundColor: "#e9f5ff" },
  //       cellStyle: {
  //         padding: "12px 8px",
  //         textAlign: "left",
  //         whiteSpace: "normal",
  //         lineHeight: "1.4",
  //         display: "flex",
  //         alignItems: "flex-start",
  //       } as any,
  //     },
  //     {
  //       field: "location",
  //       headerName: "Location",
  //       flex: 1,
  //       minWidth: 150,
  //       wrapText: true,
  //       autoHeight: true,
  //       headerStyle: { backgroundColor: "#e9f5ff" },
  //       cellStyle: {
  //         padding: "12px 8px",
  //         textAlign: "left",
  //         whiteSpace: "normal",
  //         lineHeight: "1.4",
  //         display: "flex",
  //         alignItems: "center",
  //       },
  //     },
  //     {
  //       field: "qty",
  //       headerName: "Quantity",
  //       flex: 0.5,
  //       minWidth: 100,
  //       headerStyle: { backgroundColor: "#e9f5ff" },
  //       cellStyle: {
  //         padding: "12px 8px",
  //         textAlign: "center",
  //         display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
  //       },
  //     },
  //   ];
  // }

  // Heat Detector variations (heat detector, heat detectors, etc.)
  if (name.includes("heat")) {
    return [
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "flex-start",
        } as any,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "qty",
        headerName: "Quantity",
        flex: 0.5,
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    ];
  }

  // Sprinkler variations (sprinkler, sprinklers, etc.)
  if (name.includes("sprinkler")) {
    return [
      // {
      //   field: "description",
      //   headerName: "Description",
      //   flex: 2,
      //   minWidth: 200,
      //   wrapText: true,
      //   autoHeight: true,
      //   headerStyle: { backgroundColor: "#e9f5ff" },
      //   cellStyle: {
      //     padding: "12px 8px",
      //     textAlign: "left",
      //     whiteSpace: "normal",
      //     lineHeight: "1.4",
      //     display: "flex",
      //     alignItems: "flex-start",
      //   } as any,
      // },
      {
        field: "equipmentInventory",
        headerName: "Equipment",
        minWidth: 200,
        flex: 2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "flex-start",
        } as any,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        },
      },
      // {
      //   field: "qty",
      //   headerName: "Quantity",
      //   flex: 0.5,
      //   minWidth: 100,
      //   headerStyle: { backgroundColor: "#e9f5ff" },
      //   cellStyle: {
      //     padding: "12px 8px",
      //     textAlign: "center",
      //     display: "flex",
      //     alignItems: "center",
      //     justifyContent: "center",
      //   },
      // },
    ];
  }

  // Control Panel variations (control panel, control panels, fire pump, pump, etc.)
  if (
    name.includes("control") ||
    name.includes("panel") ||
    name.includes("pump")
  ) {
    return [
      // {
      //   field: "description",
      //   headerName: "Description",
      //   flex: 2,
      //   minWidth: 200,
      //   wrapText: true,
      //   autoHeight: true,
      //   headerStyle: { backgroundColor: "#e9f5ff" },
      //   cellStyle: {
      //     padding: "12px 8px",
      //     textAlign: "left",
      //     whiteSpace: "normal",
      //     lineHeight: "1.4",
      //     display: "flex",
      //     alignItems: "flex-start",
      //   } as any,
      // },
      {
        field: "equipmentInventory",
        headerName: "Equipment",
        minWidth: 200,
        flex: 2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        } as any,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        },
      },
      // {
      //   field: "qty",
      //   headerName: "Quantity",
      //   flex: 0.5,
      //   minWidth: 100,
      //   headerStyle: { backgroundColor: "#e9f5ff" },
      //   cellStyle: {
      //     padding: "12px 8px",
      //     textAlign: "center",
      //     display: "flex",
      //     alignItems: "center",
      //     justifyContent: "center",
      //   },
      // },
    ];
  }

  // Hose variations (hose, hoses, fire hose, etc.)
  if (name.includes("hose")) {
    return [
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "flex-start",
        } as any,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        } as any,
      },
      {
        field: "qty",
        headerName: "Quantity",
        flex: 0.5,
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
      },
    ];
  }

  // Alarm variations (alarm, alarms, fire alarm, etc.)
  if (name.includes("alarm")) {
    return [
      {
        field: "panelNo",
        headerName: "Panel No",
        flex: 0.5,
        minWidth: 100,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          whiteSpace: "normal",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          wordWrap: "break-word",
        } as any,
      },
      {
        field: "loopNo",
        headerName: "Loop No",
        flex: 0.5,
        minWidth: 100,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          whiteSpace: "normal",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          wordWrap: "break-word",
        } as any,
      },
      {
        field: "deviceNo",
        headerName: "Device No",
        flex: 0.5,
        minWidth: 100,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          whiteSpace: "normal",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          wordWrap: "break-word",
        } as any,
      },
      {
        field: "loopType",
        headerName: "Loop Type",
        flex: 0.5,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          whiteSpace: "normal",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          wordWrap: "break-word",
        } as any,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.5",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          wordWrap: "break-word",
        } as any,
      },
    ];
  }

  if (name.includes("hydrant")) {
    //For Hydrants
    return [
      {
        field: "equipmentInventory",
        headerName: "Equipment",
        minWidth: 200,
        flex: 2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        } as any,
      },
      {
        field: "HB_No",
        headerName: "HB No",
        flex: 1,
        minWidth: 100,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        } as any,
        cellRenderer: (params: any) => (
          <div
            style={{
              whiteSpace: "normal",
              lineHeight: "1.4",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              padding: "4px 0",
              maxWidth: "100%",
            }}
            title={params.value}
          >
            {params.value || "N/A"}
          </div>
        ),
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          whiteSpace: "normal",
          lineHeight: "1.4",
          display: "flex",
          alignItems: "center",
        } as any,
      },
      {
        field: "floor",
        headerName: "Floor",
        flex: 1,
        minWidth: 80,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
      },
    ];
  }

  return [];
};

const getDefaultColumns = (): ColDef[] => [
  {
    field: "equipmentInventory",
    headerName: "Equipment",
    minWidth: 200,
    flex: 2,
    headerStyle: { backgroundColor: "#e9f5ff" },
    filter: false,
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      padding: "12px 8px",
      textAlign: "left",
      whiteSpace: "normal",
      lineHeight: "1.4",
      display: "flex",
      alignItems: "flex-start",
    } as any,
  },
  {
    field: "location",
    headerName: "Location",
    headerStyle: { backgroundColor: "#e9f5ff" },
    flex: 1.5,
    minWidth: 150,
    filter: false,
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      padding: "12px 8px",
      textAlign: "left",
      whiteSpace: "normal",
      lineHeight: "1.4",
      display: "flex",
      alignItems: "center",
    } as any,
    cellRenderer: (params: any) => (
      <div
        style={{
          whiteSpace: "normal",
          lineHeight: "1.4",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          padding: "4px 0",
          maxWidth: "100%",
        }}
        title={params.data?.location || params.data?.areaName || "N/A"}
      >
        {params.data?.location || params.data?.areaName || "N/A"}
      </div>
    ),
  },
  {
    field: "floor",
    headerName: "Floor",
    headerStyle: { backgroundColor: "#e9f5ff" },
    flex: 1,
    minWidth: 80,
    filter: false,
    cellStyle: {
      padding: "12px 8px",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    } as any,
  },
];

const SafetyEquipment: React.FC = () => {
  const { equipmentTypeId } = useParams<{ equipmentTypeId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const equipmentTypeName =
    location.state?.equipmentTypeName || equipmentTypeId?.replace(/-/g, " ");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [qrCodeData, setQrCodeData] = useState<any>(null);
  const [showQRModal, setShowQRModal] = useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [paginatedAssets, setPaginatedAssets] = useState<any[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<string>("");
  const [sortModel, setSortModel] = useState<string>("");

  const sortField = sortModel ? sortModel.split(" ")[0] : "";
  const sortDirection: "ASC" | "DESC" = sortModel?.split(" ")[1] === "ASC" ? "ASC" : "DESC";

  // Utility function to format ISO date to date only
  const formatDateOnly = (isoDateString: string) => {
    if (!isoDateString) return "N/A";
    return isoDateString.split("T")[0] || "N/A";
  };

  // Utility: check if a given date is overdue or within a warning window (in days)
  const isDateDueOrNear = (
    dateInput?: string | Date | null,
    daysWindow: number = 7
  ): boolean => {
    if (!dateInput) return false;

    const date =
      typeof dateInput === "string" ? new Date(dateInput) : new Date(dateInput);
    if (isNaN(date.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = new Date(date);
    due.setHours(0, 0, 0, 0);

    const diffDays = (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);

    // Overdue (diffDays < 0) OR within daysWindow in the future
    return diffDays <= daysWindow;
  };

  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [premisesData, setPremisesData] = useState<PremisesData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedEquipmentType, setSelectedEquipmentType] =
    useState<string>("");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [exportComment, setExportComment] = useState<string>("");
  const [exportConductedbyName, setExportConductedbyName] = useState<string>("");
  const [exportContractor, setExportContractor] = useState<string>("");
  const [showDashboard, setShowDashboard] = useState<boolean>(false);
  const [assetAssessments, setAssetAssessments] = useState<Map<string, any>>(
    new Map(),
  );
  const { allPremises, fetchPremises } = usePremisesStore();
  const { roleData } = useAuth();
  const {
    equipmentData,
    allAssetsEquipment,
    allAssetSubcategories,
    assetSubcategoryData,
  } = useEquipmentStore();
  const {
    getAssetsByIdHandler,
    getMaintenanceByIdHandler,
    getAllMaintenanceHandler,
    clearData,
  } = useAsset();
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();

  useEffect(() => {
  }, [equipmentTypeName, selectedEquipmentType]);

  // Paginated asset fetcher
  const fetchPaginatedAssets = useCallback(async () => {
    try {
      setIsLoading(true);

      let equipmentInventoryId = "";
      if (equipmentTypeName) {
        const match = equipmentData.find(
          (eq: any) =>
            eq.equipmentName?.toLowerCase() === equipmentTypeName.toLowerCase()
        );
        if (match) equipmentInventoryId = match._id || "";
      }

      const params: PaginatedAssetsParams = {
        page: currentPage,
        limit: pageSize,
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
        ...(equipmentInventoryId && {
          equipmentInventory: equipmentInventoryId,
        }),
        ...(selectedFloor &&
          selectedFloor !== "Select Floor" && { floor: selectedFloor }),
        ...(selectedRegion && { region: selectedRegion }),
        ...(selectedPremises && { premisesId: selectedPremises }),
        ...(sortModel && { sort: sortModel }),
      };
      const response = await getPaginatedAssets(params);
      const data = response.data;

      setPaginatedAssets(data.message || []);
      setTotalRecords(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error("Error fetching paginated assets:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    searchTerm,
    equipmentTypeName,
    equipmentData,
    selectedFloor,
    selectedRegion,
    selectedPremises,
    sortModel,
  ]);

  // Fetch assessments for the current page's assets
  useEffect(() => {
    const fetchAssessments = async () => {
      if (paginatedAssets.length === 0) {
        setAssetAssessments(new Map());
        return;
      }
      try {
        const assetIds = paginatedAssets.map(
          (a: any) => a._id || a.id
        );
        const response = await axiosInstance.get(
          "/api/inspection-assessments",
          {
            params: { limit: 1000 },
          },
        );

        const responseData = response.data.data;
        const assessments = Array.isArray(responseData)
          ? responseData
          : responseData?.assessments || [];

        const nonDraftAssessments = assessments.filter(
          (assessment: any) =>
            !assessment.isDraft &&
            assetIds.includes(assessment.assetId)
        );

        const assessmentMap = new Map<string, any>();
        nonDraftAssessments.forEach((assessment: any) => {
          const assetId = assessment.assetId;
          const existing = assessmentMap.get(assetId);
          const assessmentDate = new Date(
            assessment.inspectionDate || assessment.createdAt,
          );
          const existingDate = existing
            ? new Date(existing.inspectionDate || existing.createdAt)
            : null;

          if (!existing || !existingDate || assessmentDate > existingDate) {
            assessmentMap.set(assetId, assessment);
          }
        });

        setAssetAssessments(assessmentMap);
      } catch (error) {
        console.error("Error fetching assessments:", error);
      }
    };

    fetchAssessments();
  }, [paginatedAssets]);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchPremises(),
          allAssetsEquipment(),
          allAssetSubcategories(),
        ]);
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };
    initializeData();
  }, [fetchPremises, allAssetsEquipment, allAssetSubcategories]);

  // Fetch paginated assets whenever filters/page change
  useEffect(() => {
    fetchPaginatedAssets();
  }, [fetchPaginatedAssets]);

  // Build premises data for local filtering (region/premises)
  useEffect(() => {
    if (allPremises.length > 0) {
      const mappedPremisesData: PremisesData[] = allPremises.map(
        (premise: any) => ({
          id: premise._id || premise.id,
          premisesName: premise.premisesName,
          addressLine1: premise.addressLine1,
          townCity: premise.townCity,
          region: premise.region,
          postcode: premise.postcode,
        }),
      );
      setPremisesData(mappedPremisesData);
    }
  }, [allPremises]);

  const processedData = useMemo(() => {
    if (!paginatedAssets.length) {
      return [];
    }

    const premiseMap = new Map(
      allPremises.map((premise) => [
        premise._id,
        premise.premisesName?.trim() || premise.premisesName,
      ]),
    );
    const equipmentMap = new Map(
      equipmentData.map((equipment) => [
        equipment._id,
        equipment.equipmentName,
      ]),
    );
    const equipmentSubcategoryMap = new Map(
      assetSubcategoryData.map((subcategory) => [
        subcategory._id,
        subcategory.subcategoryName,
      ]),
    );

    if (equipmentTypeName) {
      setSelectedEquipmentType(equipmentTypeName);
    }

    let mapped = paginatedAssets.map((item: any) => {
      const premiseName =
        premiseMap.get(item.premisesId) ||
        premiseMap.get(item.premisesName) ||
        item.premisesName;
      const equipmentName =
        equipmentMap.get(item.equipmentInventory) ||
        item.equipmentInventory?.equipmentInventory;
      const equipmentSubcategoryName =
        equipmentSubcategoryMap.get(item.equipmentSubcategory) ||
        item.equipmentSubcategory?.equipmentSubcategory;

      const latestCheck =
        Array.isArray(item.checksAssets) && item.checksAssets.length > 0
          ? (() => {
            const sortedChecks = item.checksAssets.sort((a: any, b: any) => {
              if (a.createdAt && b.createdAt) {
                return (
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
                );
              }
              return b._id?.localeCompare(a._id) || 0;
            });
            return sortedChecks[0];
          })()
          : null;

      // Check 3 critical questions for non-compliance — exact match
      const assessment = assetAssessments.get(item._id);
      let hasNonCompliant = false;
      if (assessment?.responses && Array.isArray(assessment.responses)) {
        const CRITICAL_QUESTIONS = [
          'Is the smoke detector in healthy and in working condition ?',
          'Refilling of Extinguisher done in 5 year span or lesser period than that ?',
          'HPT/ Hydrostatic Testing done within 5 years or lesser period than that according to OEM?',
        ];
        hasNonCompliant = assessment.responses.some((r: any) => {
          const q = (r.question || '').trim();
          const isCritical = CRITICAL_QUESTIONS.some(
            (cq) => cq.toLowerCase() === q.toLowerCase()
          );
          if (!isCritical) return false;
          const val = r.value;
          const st = r.status;
          return (
            val === 'not_complied' ||
            val === 'partial_complied' ||
            val === false ||
            st === 'fail' ||
            st === 'attention'
          );
        });
      }

      // Compute refill / HPT requirements (for fire extinguishers and others that use these dates)
      const refillRequired = isDateDueOrNear(
        // Prefer explicit nextRefillDueDate if present, fall back to lastRefillDate with +1 year
        item.nextRefillDueDate || new Date(item.lastRefillDate || "").setFullYear(new Date(item.lastRefillDate || "").getFullYear() + 1) || null
      );

      const hptRequired = isDateDueOrNear(
        item.nextServiceDueDate || new Date(item.hptTestDate || "").setFullYear(new Date(item.hptTestDate || "").getFullYear() + 1) || null
      );

      return {
        equipmentInventory: equipmentName || "N/A",
        capacity: item.capacity || "N/A",
        hptTestDate: formatDateOnly(item.hptTestDate || "N/A"),
        location: item.location || item.areaName || "N/A",
        equipmentManufactureDate: item.equipmentManufactureDate || "N/A",
        cylinderNumber: item.cylinderNumber || "N/A",
        type: item.type || "N/A",
        lastRefillDate: formatDateOnly(item.lastRefillDate || "N/A"),
        floor: item.floor || "N/A",
        id: item._id,
        description: item.Description || "N/A",
        qty: item.Qty || "N/A",
        equipmentSubcategory: equipmentSubcategoryName || "N/A",
        panelNo: item.panelNo || "N/A",
        loopNo: item.loopNo || "N/A",
        deviceNo: item.deviceNo || "N/A",
        loopType: item.loopType || "N/A",
        allChecks: item.checksAssets || [],
        HB_No: item.HB_No || "N/A",
        amcStatus: item.amcStatus || "N/A",
        amcVendor: item.amcVendor || "N/A",
        amcStartDate: formatDateOnly(item.amcStartDate || "N/A"),
        amcEndDate: formatDateOnly(item.amcEndDate || "N/A"),
        toBeReplaced: item.toBeReplaced || false,
        refillRequired,
        hptRequired,
        hasNonCompliant,
      };
    });

    return mapped;
  }, [
    paginatedAssets,
    allPremises,
    equipmentData,
    equipmentTypeName,
    assetSubcategoryData,
    assetAssessments,
  ]);

  useEffect(() => {
    if (processedData.length > 0 || !isLoading) {
      setRowData(processedData);
    }
  }, [processedData, isLoading]);

  const selectedEquipmentCategoryId = useMemo(() => {
    const match = equipmentData.find(
      (item) => item.equipmentName === selectedEquipmentType,
    );
    return match ? match._id : "";
  }, [selectedEquipmentType, equipmentData]);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const handleDownload = async () => {
    setLoading(true);
    setStatus("Fetching all inspection data...");

    try {
      const response = await axiosInstance.get("api/reports/download-all", {
        responseType: "blob",
      });

      setStatus("Generating ZIP...");

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `All_Equipment_Reports_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setStatus("");
    } catch (err: any) {
      console.error(err);
      setStatus(err?.response?.data?.message || "Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Single QR code handler for both preview and download
  const handleQRCode = useCallback(
    async (
      assetId: string,
      action: "preview" | "download" = "preview",
      format: "png" | "svg" = "png",
    ) => {
      try {
        setIsLoading(true);

        if (action === "download") {
          // Download directly
          const response = await axiosInstance.get(
            `/api/assets/${assetId}/qr-code?download=true&format=${format}`,
            {
              responseType: "blob",
            },
          );

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `asset-qr-${assetId}.${format}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          // Preview - get QR code data
          const response = await axiosInstance.get(
            `/api/assets/${assetId}/qr-code?download=false`,
          );

          // Display the QR code in modal
          setQrCodeData({
            ...response.data,
            assetName:
              processedData.find((asset) => asset.id === assetId)
                ?.equipmentInventory || "Asset",
          });
          setShowQRModal(true);
        }
      } catch (error) {
        console.error("Error with QR code:", error);
        alert(`Failed to ${action} QR code`);
      } finally {
        setIsLoading(false);
      }
    },
    [processedData],
  );

  // Bulk QR code download
  const handleBulkDownloadQR = useCallback(async () => {
    if (selectedIds.length === 0) {
      alert("Please select assets to download QR codes");
      return;
    }

    try {
      setIsLoading(true);

      // Download QR codes for all selected assets
      for (const assetId of selectedIds) {
        await handleQRCode(assetId, "download", "png");
        // Add a small delay to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      alert(`QR codes downloaded for ${selectedIds.length} assets`);
    } catch (error) {
      console.error("Error with bulk QR code download:", error);
      alert("Failed to download some QR codes");
    } finally {
      setIsLoading(false);
    }
  }, [selectedIds, handleQRCode]);

  const handleExportExcel = useCallback(async (comment?: string, contractor?: string, condutedBy?: string) => {
    try {
      setIsExporting(true);
      setShowExportModal(false);

      let equipmentInventoryId = "";
      if (equipmentTypeName) {
        const match = equipmentData.find(
          (eq: any) =>
            eq.equipmentName?.toLowerCase() === equipmentTypeName.toLowerCase()
        );
        if (match) equipmentInventoryId = match._id || "";
      }

      const params: any = {
        ...(searchTerm.trim() && { search: searchTerm.trim() }),
        ...(equipmentInventoryId && {
          equipmentInventory: equipmentInventoryId,
          equipmentName: equipmentTypeName,
        }),
        ...(selectedFloor &&
          selectedFloor !== "Select Floor" && { floor: selectedFloor }),
        ...(selectedPremises && { premisesId: selectedPremises }),
        ...(sortModel && { sort: sortModel }),
        ...(comment?.trim() && { comment: comment.trim() }),
        ...(contractor?.trim() && { contractor: contractor.trim() }),
        ...(condutedBy?.trim() && { condutedBy: condutedBy.trim() }),
      };

      const queryString = new URLSearchParams(params).toString();
      const url = `/api/assets/export/excel?${queryString}`;

      const response = await axiosInstance.get(url, {
        responseType: "blob",
      });

      const fileName = "fire_extinguisher_maintenance_and_inspection_report.xlsx";

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading excel report:", error);
      alert("Failed to download Excel report.");
    } finally {
      setIsExporting(false);
      setExportComment("");
      setExportConductedbyName("");
      setExportContractor("");
    }
  }, [
    searchTerm,
    equipmentTypeName,
    equipmentData,
    selectedFloor,
    selectedPremises,
    sortModel,
  ]);

  // Define column configurations for different equipment types
  const getColumnDefinitions = (equipmentType: string): ColDef[] => {
    const commonColumns = [
      ...(roleData?.roleName === "maintenance"
        ? [
          {
            field: "id",
            headerName: "",
            sortable: false,
            flex: 0.4,
            headerStyle: { backgroundColor: "#e9f5ff" },
            filter: false,
            cellStyle: { padding: "10px 0 0 5px " },
            cellRenderer: (params: ICellRendererParams) => (
              <div className="flex gap-x-3 pt-2">
                <input
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds((prev) => {
                        const prevArray = Array.isArray(prev)
                          ? prev
                          : prev
                            ? [prev]
                            : [];
                        return [...prevArray, params.data.id];
                      });
                    } else {
                      setSelectedIds((prev) => {
                        const prevArray = Array.isArray(prev)
                          ? prev
                          : prev
                            ? [prev]
                            : [];
                        return prevArray.filter(
                          (id) => id !== params.data.id,
                        );
                      });
                    }
                  }}
                  checked={selectedIds.includes(params.data.id)}
                  type="checkbox"
                  name={params.data.id}
                  id={params.data.id}
                  className="cursor-pointer"
                />
              </div>
            ),
          },
        ]
        : []),
    ];

    const actionColumn: ColDef = {
      field: "action",
      headerName: "Action",
      sortable: false,
      cellStyle: {
        padding: "12px 8px",
        textAlign: "center",
        whiteSpace: "normal",
        lineHeight: "1.5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      } as any,
      minWidth: 200,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1.2,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className="flex gap-x-3 justify-center items-center">
            {canRead(ASSETS_EQUIPMENT) && (
              <button
                onClick={() => handleView(params.data.id)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="View Asset"
              >
                <img className="w-5 h-5" src={images.viewIcon} alt="View" />
              </button>
            )}
            {canUpdate(ASSETS_EQUIPMENT) && (
              <button
                onClick={() => handleEdit(params.data.id)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Edit Asset"
              >
                <img className="w-5 h-5" src={images.editIcon} alt="Edit" />
              </button>
            )}
            {/* QR Code Actions */}
            <button
              onClick={() => handleQRCode(params.data.id, "preview")}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Preview QR Code"
            >
              <QrCode className="w-5 h-5 text-blue-600" />
            </button>
          </div>
        );
      },
      filter: false,
    };



    // To Be Replaced column (read-only — updated via Inspection)
    const toBeReplacedColumn: ColDef = {
      field: "toBeReplaced",
      headerName: "To be Replaced?",
      sortable: true,
      minWidth: 100,
      maxWidth: 120,
      flex: 0.5,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "12px 8px",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      } as any,
      cellRenderer: (params: ICellRendererParams) => {
        const isMarked = params.data?.toBeReplaced;
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${isMarked
              ? "bg-red-100 text-red-700 border border-red-300"
              : "bg-gray-100 text-gray-500 border border-gray-200"
              }`}
            title={isMarked ? "Marked for replacement during inspection" : "Not marked for replacement"}
          >
            {isMarked ? "Yes" : "No"}
          </span>
        );
      },
    };

    // New Inspection & Maintenance column
    const inspectionMaintenanceColumn: ColDef = {
      field: "inspectionMaintenance",
      headerName: "Inspection & Maintenance",
      sortable: false,
      cellStyle: {
        padding: "12px 8px",
        textAlign: "center",
        whiteSpace: "normal",
        lineHeight: "1.5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      } as any,
      minWidth: 180,
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        // Get latest assessment for this asset
        const assetId = params.data?.id || params.data?._id;
        const assessment = assetId ? assetAssessments.get(assetId) : null;

        // Calculate compliance status
        let complianceStatus:
          | "complied"
          | "partially-complied"
          | "not-complied"
          | "neutral" = "neutral";
        let compliancePercentage = 0;

        if (
          assessment &&
          assessment.responses &&
          Array.isArray(assessment.responses)
        ) {
          const responses = assessment.responses.filter(
            (r: any) => r.status && r.status !== "na",
          );
          const total = responses.length;

          if (total > 0) {
            const complied = responses.filter(
              (r: any) => r.status?.toLowerCase() === "pass",
            ).length;
            const partiallyComplied = responses.filter(
              (r: any) => r.status?.toLowerCase() === "attention",
            ).length;
            const notComplied = responses.filter(
              (r: any) => r.status?.toLowerCase() === "fail",
            ).length;

            compliancePercentage = Math.round((complied / total) * 100);

            if (notComplied > 0 || compliancePercentage < 50) {
              complianceStatus = "not-complied";
            } else if (partiallyComplied > 0 || compliancePercentage < 80) {
              complianceStatus = "partially-complied";
            } else {
              complianceStatus = "complied";
            }
          }
        }

        // Determine button color based on compliance status
        const getButtonClasses = () => {
          const baseClasses =
            "flex items-center gap-1 px-3 py-1 rounded hover:opacity-90 transition-all text-xs font-medium";

          switch (complianceStatus) {
            case "complied":
              return `${baseClasses} bg-green-600 text-white hover:bg-green-700`;
            case "partially-complied":
              return `${baseClasses} bg-yellow-500 text-white hover:bg-yellow-600`;
            case "not-complied":
              return `${baseClasses} bg-red-600 text-white hover:bg-red-700`;
            default:
              return `${baseClasses} bg-[#00A8A5] text-white hover:bg-[#008A87]`;
          }
        };

        const getTitle = () => {
          if (complianceStatus === "neutral") {
            return "Start Assessment - Auto-loads checklist for this asset type";
          }
          return `Assessment - ${compliancePercentage}% Complied (${complianceStatus === "complied"
            ? "Fully Complied"
            : complianceStatus === "partially-complied"
              ? "Partially Complied"
              : "Not Complied"
            })`;
        };

        return (
          <div className="flex gap-x-2 justify-center items-center">
            {/* Assessment Action */}
            <button
              onClick={() => {
                try {
                  if (params.data?.id) {
                    // Find the original asset data from paginated assets
                    const originalAsset = paginatedAssets.find(
                      (asset: any) =>
                        (asset._id || asset.id) === params.data.id
                    );

                    navigate(`/safety-equipment/inspection/${params.data.id}`, {
                      state: {
                        assetData: originalAsset,
                        displayData: params.data,
                      },
                    });
                  } else {
                    console.error("Asset ID not found");
                    alert("Unable to start inspection: Asset ID not found");
                  }
                } catch (error) {
                  console.error("Error navigating to inspection:", error);
                  alert("Error starting inspection. Please try again.");
                }
              }}
              className={getButtonClasses()}
              title={getTitle()}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              <span>Assessment</span>
              {complianceStatus !== "neutral" && (
                <span className="ml-1 text-xs opacity-90">
                  ({compliancePercentage}%)
                </span>
              )}
            </button>
          </div>
        );
      },
      filter: false,
    };

    const equipmentTypeColumns = getEquipmentTypeColumns(equipmentType);
    if (equipmentTypeColumns.length > 0) {
      return [
        ...commonColumns,
        ...equipmentTypeColumns,
        toBeReplacedColumn,
        inspectionMaintenanceColumn,
        actionColumn,
      ];
    }
    return [
      ...commonColumns,
      ...getDefaultColumns(),
      toBeReplacedColumn,
      inspectionMaintenanceColumn,
      actionColumn,
    ];
  };

  const columnDefs: ColDef[] = useMemo(() => {
    const columns = getColumnDefinitions(equipmentTypeName);
    return columns;
  }, [equipmentTypeName, rowData, roleData, assetAssessments, paginatedAssets]);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      filter: false,
      sortable: false,
    }),
    [],
  );

  // Optimized event handlers with useCallback
  const handleView = useCallback(
    (id: string) => {
      setIsCreating(true);
      setIsView(true);
      setIsEdit(false);
      getAssetsByIdHandler(id);
      if (roleData?.roleName === "maintenance") {
        getAllMaintenanceHandler(id);
        getMaintenanceByIdHandler(id);
      }
    },
    [
      getAssetsByIdHandler,
      getAllMaintenanceHandler,
      getMaintenanceByIdHandler,
      roleData,
    ],
  );

  const handleEdit = useCallback(
    (id: string) => {
      if (roleData?.roleName === "maintenance") {
        getAllMaintenanceHandler(id);
        getMaintenanceByIdHandler(id);
      }
      getAssetsByIdHandler(id);
      setIsCreating(true);
      setIsEdit(true);
      setIsView(false);
    },
    [
      getAllMaintenanceHandler,
      getAssetsByIdHandler,
      getMaintenanceByIdHandler,
      roleData,
    ],
  );

  const handleRegionChange = useCallback((value: string) => {
    setSelectedRegion(value);
    setSelectedPremises("");
    setCurrentPage(1);
  }, []);

  const handlePremisesChange = useCallback((value: string) => {
    setSelectedPremises(value);
    setCurrentPage(1);
  }, []);

  const handleFloorChange = useCallback((value: string) => {
    setSelectedFloor(value === "Select Floor" ? "" : value);
    setCurrentPage(1);
  }, []);

  const handleEquipmentChange = useCallback(
    (value: string) => {
      if (value === "") {
        setSelectedEquipmentType("");
      } else {
        const selectedEquipment = equipmentData.find(
          (equipment: any) => equipment._id === value,
        );
        setSelectedEquipmentType(
          selectedEquipment ? selectedEquipment.equipmentName : ""
        );
      }
      setCurrentPage(1);
    },
    [equipmentData]
  );

  const handleSubCategoryChange = useCallback((value: string) => {
    setSelectedSubCategory(value);
  }, []);

  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 400);
  }, []);

  const handleCreate = useCallback(() => {
    setIsCreating(true);
    setIsEdit(false);
    setIsView(false);
    clearData();
  }, [clearData]);

  const handleMaintenance = useCallback(() => {
    setIsMaintenance(true);
    setIsEdit(false);
    setIsView(false);
    clearData();
  }, [clearData]);

  const onGridReady = useCallback((e: GridReadyEvent) => {
    e.api.sizeColumnsToFit();
  }, []);

  const handleSortChange = useCallback(
    (field: string, direction: "ASC" | "DESC") => {
      const newSort = field ? `${field} ${direction}` : "";
      setSortModel(newSort);
      setCurrentPage(1);
    },
    []
  );

  const handleImportAssetsHandler = useCallback(
    async (formData: FormData) => {
      try {
        setIsLoading(true);
        await axiosInstance.post(`/api/upload-bulk-assets`, formData);

        setCurrentPage(1);
        fetchPaginatedAssets();
        alert("Assets imported successfully!");
      } catch (error) {
        console.error("Import failed:", error);
        alert("Import failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPaginatedAssets]
  );

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloading(true);
    try {
      const assetType = equipmentTypeName || "safety-equipment";
      const sanitizedAssetType = assetType
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, "-")
        .toLowerCase();

      const response = await axiosInstance.get(
        `/api/download-template/${assetType}`,
        {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = `${sanitizedAssetType}_Upload_Template.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      alert("Failed to download template. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }, [equipmentTypeName]);

  return (
    <>
      <div className="w-full px-2">
        <div className="page-header mt-20 flex flex-col gap-4">
          <div className="flex items-center justify-between px-2 mb-2">
            <h2 className="header-title text-xl font-semibold">
              {equipmentTypeName
                ? `${equipmentTypeName} - Inventory & Maintenance`
                : "Fire Safety Equipment Inventory & Maintenance"}
            </h2>
            <button
              onClick={() => navigate("/safety-equipment")}
              className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Equipment Types
            </button>
          </div>

          <div className="flex items-center justify-between px-2">
            <div className="flex gap-4">
              <Dropdown
                selectedValue={selectedRegion}
                onChange={handleRegionChange}
                type="region"
              />
              <Dropdown
                selectedValue={selectedPremises}
                onChange={handlePremisesChange}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDashboard(!showDashboard)}
                className={`flex items-center gap-2 py-2 px-4 rounded transition-colors duration-200 ${showDashboard
                  ? "bg-[#00A8A5] text-white hover:bg-[#008A87]"
                  : "bg-[#00A8A5] text-white hover:bg-[#008A87]"
                  }`}
                title={showDashboard ? "Hide Dashboard" : "Show Dashboard"}
              >
                {showDashboard ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Hide Dashboard
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Show Dashboard
                  </>
                )}
              </button>
              <button
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download Template
                  </>
                )}
              </button>
              <button
                onClick={() => setUploadModalOpen(true)}
                className="py-2 px-4 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
              >
                Import Assets
              </button>
            </div>
          </div>
        </div>

        <EquipmentDashboard
          isVisible={showDashboard}
          currentEquipmentType={equipmentTypeName}
        />

        <div className="tableBlock w-full">
          <div className="card">
            <div className="tableSearchBlock d-flex justify-content-between">
              <div className="leftTableSearchBlock flex gap-4">
                <div className="input-affix">
                  <i className="prefix-icon anticon anticon-search"></i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    onChange={handleSearch}
                  />
                </div>
                <div className="flex items-center">
                  <div className="flex items-center gap-3 bg-white border-2 border-[#a5b7cf] rounded-lg px-2 py-2 w-48 relative">
                    <select
                      onChange={(e) => handleFloorChange(e.target.value)}
                      className="text-md text-[#5a687c] font-medium outline-none flex-1 px-2 appearance-none bg-transparent"
                    >
                      <option value="Select Floor">Select Floor</option>
                      <option value="Basement">Basement</option>
                      <option value="Floor 1">Floor 1</option>
                      <option value="Floor 2">Floor 2</option>
                      <option value="Floor 3">Floor 3</option>
                      <option value="Floor 4">Floor 4</option>
                      <option value="Floor 5">Floor 5</option>
                      <option value="Floor 6">Floor 6</option>
                      <option value="Floor 7">Floor 7</option>
                      <option value="Floor 8">Floor 8</option>
                      <option value="Floor 9">Floor 9</option>
                      <option value="Terrace">Terrace</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-4 pointer-events-none" />
                  </div>
                </div>

                {/* Sort Controls */}
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-2 bg-white border-2 border-[#a5b7cf] rounded-lg px-2 py-2 relative">
                    <ArrowUpDown className="w-4 h-4 text-gray-500 shrink-0" />
                    <select
                      value={sortField}
                      onChange={(e) =>
                        handleSortChange(e.target.value, sortDirection)
                      }
                      className="text-md text-[#5a687c] font-medium outline-none appearance-none bg-transparent pr-6"
                    >
                      <option value="">Sort By</option>
                      <option value="cylinderNumber">Cylinder No</option>
                      <option value="capacity">Capacity</option>
                      <option value="type">Type</option>
                      <option value="floor">Floor</option>
                      <option value="location">Location</option>
                      <option value="hptTestDate">HPT Date</option>
                      <option value="lastRefillDate">Last Refill Date</option>
                      <option value="createdAt">Created Date</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 pointer-events-none" />
                  </div>
                  {sortField && (
                    <button
                      onClick={() =>
                        handleSortChange(
                          sortField,
                          sortDirection === "ASC" ? "DESC" : "ASC"
                        )
                      }
                      className="flex items-center justify-center w-10 h-10 bg-white border-2 border-[#a5b7cf] rounded-lg hover:bg-gray-50 transition-colors"
                      title={sortDirection === "ASC" ? "Ascending" : "Descending"}
                    >
                      {sortDirection === "ASC" ? (
                        <ArrowUp className="w-4 h-4 text-[#00A8A5]" />
                      ) : (
                        <ArrowDown className="w-4 h-4 text-[#00A8A5]" />
                      )}
                    </button>
                  )}
                </div>

                {selectedEquipmentType && (
                  <div className="flex items-center w-full">
                    <div className="relative w-full">
                      <select
                        name="subCategory"
                        id="subCategory"
                        value={selectedSubCategory}
                        onChange={(e) =>
                          handleSubCategoryChange(e.target.value)
                        }
                        className="w-full text-md text-[#5a687c] font-medium outline-none bg-white border-2 border-[#a5b7cf] rounded-lg px-4 py-2 pr-10 appearance-none cursor-pointer"
                      >
                        <option value="">Select SubCategory</option>
                        {assetSubcategoryData
                          .filter(
                            (subcat) =>
                              subcat.categoryId === selectedEquipmentCategoryId,
                          )
                          .map((subcat, idx) => (
                            <option key={subcat._id || idx} value={subcat._id}>
                              {subcat.subcategoryName}
                            </option>
                          ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                )}
              </div>

              <div className="rightTableSearchBlock d-flex gap-4">
                {/* Bulk QR Code Download */}
                {selectedIds.length > 0 && (
                  <button
                    onClick={handleBulkDownloadQR}
                    disabled={isLoading}
                    className="flex items-center gap-2 py-2 px-4 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    Download QR Codes ({selectedIds.length})
                  </button>
                )}

                {roleData?.roleName === "maintenance" ? (
                  <div className="dashHeaderRight flex items-center gap-2">
                    {/* Export Excel Button */}
                    <button
                      type="button"
                      onClick={() => setShowExportModal(true)}
                      disabled={isExporting}
                      className="flex items-center btn-primary gap-2 py-2 px-4 text-white rounded disabled:opacity-50"
                      title="Export Data to Excel"
                    >
                      <Download className="w-4 h-4" />
                      <span>{isExporting ? "Exporting..." : "Export"}</span>
                    </button>
                    {canUpdate(ASSETS_EQUIPMENT) && (
                      <button
                        type="button"
                        onClick={handleMaintenance}
                        className="flex mr-2 btn-primary roundedBt iconWithBt text-uppercase"
                      >
                        <img src={images.addPremiessIcon} alt="" />
                        <span>UPDATE Maintenance</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="dashHeaderRight flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowExportModal(true)}
                      className="flex items-center btn-primary gap-2 py-2 px-4 text-white rounded"
                      title="Export Data to Excel"
                    >
                      {/* <ArrowBigRightDash className="w-4 h-4" /> */}
                      <span>{isExporting ? "Exporting..." : "Export all Reports"}</span>
                    </button>
                    <button
                      onClick={handleDownload}
                      disabled={loading}
                      type="button"
                      className="flex items-center btn-primary gap-2 py-2 px-4 text-white rounded"
                      title="Download All"
                    >
                      {loading ? "⏳ Preparing ZIP..." : "Download All Reports"}
                    </button>
                    {status && (
                      <span style={{ fontSize: 13, color: "#6B7280" }}>{status}</span>
                    )}
                    {canCreate(ASSETS_EQUIPMENT) && (
                      <button
                        type="button"
                        onClick={handleCreate}
                        className="flex items-center btn-primary gap-2 py-2 px-4 text-white rounded iconWithBt"
                      >
                        <img src={images.addPremiessIcon} alt="" />
                        <>
                          Add Assets
                        </>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              {isLoading && (
                <div className="absolute inset-0 bg-white/70 z-10 flex justify-center items-center rounded-lg">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              )}
              <div className="ag-theme-alpine w-full h-[650px] pl-2 border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={gridTheme}
                  headerHeight={65}
                  rowHeight={80}
                  pagination={false}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  getRowStyle={(params) => {
                    if (
                      params.data?.toBeReplaced ||
                      params.data?.hasNonCompliant ||
                      params.data?.refillRequired ||
                      params.data?.hptRequired
                    ) {
                      return {
                        background: "#fce4ec",
                        borderLeft: "4px solid #e91e63",
                      };
                    }
                    return undefined;
                  }}
                />
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                recordLabel="assets"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Export Comment Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => { setShowExportModal(false); setExportComment(""); setExportContractor(""); setExportConductedbyName(""); }}
          />
          {/* Modal card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-[#00A8A5] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold text-base">Export to Excel</h3>
              </div>
              <button
                onClick={() => { setShowExportModal(false); setExportComment(""); setExportContractor(""); setExportConductedbyName(""); }}
                className="text-white/80 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 flex flex-col gap-4">
              {/* Contractor field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contractor / Internal Management <span className="text-gray-400 font-normal">(Company Name)</span>
                </label>
                <input
                  type="text"
                  value={exportContractor}
                  onChange={(e) => setExportContractor(e.target.value)}
                  placeholder="e.g. ABC Fire Safety Pvt. Ltd."
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent transition"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conducted by <span className="text-gray-400 font-normal">(Name)</span>
                </label>
                <input
                  type="text"
                  value={exportConductedbyName}
                  onChange={(e) => setExportConductedbyName(e.target.value)}
                  placeholder="Enter Name"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent transition"
                // autoFocus
                />
              </div>

              {/* Comment field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add a comment <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  rows={4}
                  value={exportComment}
                  onChange={(e) => setExportComment(e.target.value)}
                  placeholder="e.g. Monthly audit export — reviewed by Safety Team"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent resize-none transition"
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  This comment will appear as the first row in the exported sheet.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowExportModal(false); setExportComment(""); setExportContractor(""); setExportConductedbyName(""); }}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExportExcel(exportComment, exportContractor, exportConductedbyName)}
                disabled={isExporting}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-[#00A8A5] hover:bg-[#008A87] rounded-lg transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {isExporting ? "Exporting..." : "Download Excel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        qrCodeData={qrCodeData}
        onDownloadPNG={() =>
          handleQRCode(qrCodeData?.qrData?.assetId, "download", "png")
        }
        onDownloadSVG={() =>
          handleQRCode(qrCodeData?.qrData?.assetId, "download", "svg")
        }
      />

      <MaintenanceModal
        isOpen={isMaintenance}
        onClose={() => {
          setIsMaintenance(false);
          setSelectedIds([]);
          fetchPaginatedAssets();
        }}
        modalClass={"w-full relative risk-modal-close"}
      >
        <Maintenance
          setIsCreating={setIsMaintenance}
          isCreating={isMaintenance}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
        />
      </MaintenanceModal>

      <Modal
        isOpen={isCreating}
        onClose={() => {
          setIsCreating(false);
          setSelectedIds([]);
          fetchPaginatedAssets();
        }}
        modalClass={"w-full h-full relative risk-modal-close"}
      >
        {isEdit ? (
          <AddAssets
            mode="edit"
            setIsCreating={setIsCreating}
            isCreating={isCreating}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        ) : isView ? (
          <AddAssets
            mode="view"
            setIsCreating={setIsCreating}
            isCreating={isCreating}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        ) : (
          <AddAssets
            mode="create"
            setIsCreating={setIsCreating}
            isCreating={isCreating}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
      </Modal>

      <ImportAssetsModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        equipmentCategory={selectedEquipmentCategoryId}
        onImport={handleImportAssetsHandler}
      />
    </>
  );
};

export default SafetyEquipment;
